import { FC, Fragment } from 'react';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { styled, Theme } from '@mui/material/styles';

const StyledRadioGroup = styled(RadioGroup, {
  shouldForwardProp: (prop) => prop !== 'optionSpacing',
})<{
  optionSpacing: keyof Theme['spacingSizes'];
}>(({ theme, optionSpacing }) => ({
  gap: theme.spacing(theme.spacingSizes[optionSpacing]),
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '& .MuiFormControlLabel-label': {
    width: '100%',
    ...theme.typography.body2,
    color: theme.palette.grey[900],
  },
  '& .MuiTypography-root': {
    alignSelf: 'flex-start',
  },
}));

const StyledRadio = styled(Radio, {
  shouldForwardProp: (prop) => prop !== 'isPrimary',
})<{ isPrimary?: boolean }>(({ theme, isPrimary }) => ({
  alignSelf: 'flex-start',
  position: 'relative',
  bottom: '5px',
  ...(isPrimary && {
    '&.Mui-checked .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
  }),
}));

type Props = {
  name?: string;
  value: string | null;
  onChange: (value: string) => void;
  options: Array<{
    value: string;
    label: string | JSX.Element;
    Component?: JSX.Element;
  }>;
  error?: boolean;
  helperText?: string;
  direction?: 'row' | 'column';
  optionSpacing?: keyof Theme['spacingSizes'];
  disabled?: boolean;
  color?: 'primary';
};

const RadioButtons: FC<Props> = ({
  name,
  value,
  onChange,
  options,
  error,
  helperText,
  direction = 'column',
  optionSpacing = 'sm',
  disabled = false,
  color,
}) => {
  return (
    <>
      <StyledRadioGroup
        name={name}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        row={direction === 'row' ? true : false}
        optionSpacing={optionSpacing}
      >
        {options.map((option) => (
          <Fragment key={option.value}>
            <StyledFormControlLabel
              value={option.value}
              label={option.label}
              control={
                <StyledRadio size="small" isPrimary={color === 'primary'} />
              }
              disabled={disabled}
            />
            {value === option.value && option.Component}
          </Fragment>
        ))}
      </StyledRadioGroup>
      {helperText ? (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      ) : null}
    </>
  );
};

export default RadioButtons;
